<template>
  <div>
    <LineChart :data="densidadeSellRb" :options="optionsSellRebuy" />
    <p class="align-center mt-10">PMPSells: {{ kpis.pmpRebuySells }} | PMPUnits: {{ kpis.pmpRebuyUnits }} | TotalQty: {{ kpis.totalPendingRebuys }} | LLP: {{ kpisDataCalcs.llpRebuys }}</p>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import LineChart from '../generics/lineChart.vue';

export default {
  name: 'rebuySellsDensity',
  components: { LineChart },
  methods: {
    calcWeightedAvg(sellOperations, field = 'price') {
      let totalQty = 0;
      let totalValor = 0;
      sellOperations.forEach(op => {
        const value = field === 'price' ? Number(op.price) : Number(op.avgPrice); // avgPrice da unit de origem
        totalQty += Number(op.qty);
        totalValor += Number(op.qty) * value;
      });
      return totalQty > 0 ? totalValor / totalQty : 0;
    },
  },
  computed: {
    ...mapState({ marketData: state => state.marketData, kpis: state => state.kpis}),
    ...mapGetters(['rebuySellDensityByBin', 'kpisDataCalcs']),

    densidadeSellRb() {
      const bins = this.rebuySellDensityByBin;
      if (!Object.keys(bins).length) return { labels: [], datasets: [] };

      const binSize = this.marketData.priceBand.gridSize;
      const extraBins = 2;

      const binKeys = Object.keys(bins).map(Number).sort((a, b) => a - b);
      const minPrice = binKeys[0] - binSize * extraBins;
      const maxPrice = binKeys[binKeys.length - 1] + binSize * extraBins;

      const allBins = {};
      for (let p = minPrice; p <= maxPrice; p += binSize) {
        allBins[p] = bins[p] || { qty: 0, operations: [] };
      }

      const labels = Object.keys(allBins).map(bin => {
        const binNum = Number(bin);
        return `${binNum.toFixed(2)} - ${(binNum + binSize).toFixed(2)}`;
      });

      // Calcula quantidade, PMPs e LLP por faixa
      const data = [];
      const pmpSellLine = [];
      const pmpUnitLine = [];
      const llpLine = [];

      Object.values(allBins).forEach(binData => {
        const totalQty = binData.qty;

        let pmpSell = 0;
        let pmpUnit = 0;

        if (totalQty > 0 && binData.operations.length) {
          // PMP dos sells
          const totalValorSell = binData.operations.reduce((sum, op) =>
            sum + Number(op.qty) * Number(op.price), 0
          );
          pmpSell = totalValorSell / totalQty;

          // PMP das units
          const totalValorUnit = binData.operations.reduce((sum, op) =>
            sum + Number(op.qty) * Number(op.unitAvgPrice), 0 //unitAvgPrice vêm do getter no store
          );
          pmpUnit = totalValorUnit / totalQty;
        }

        // LLP da faixa - Locked Loss Ponderado pelo Volume
        const llp = (pmpSell - pmpUnit) * totalQty;

        data.push(totalQty);
        pmpSellLine.push(pmpSell);
        pmpUnitLine.push(pmpUnit);
        llpLine.push(llp);
      });

      return {
        labels,
        datasets: [
          {
            label: 'PMP Rebuys Sells',
            data,
            fill: true,
            backgroundColor: 'rgba(255, 206, 86, 0.4)',
            borderColor: 'rgba(255, 206, 86, 1)',
            tension: 0.3,
            pointRadius: 0,
            type: 'line',
            yAxisID: 'y'
          },
          // {
          //   label: 'PMP Sells',
          //   data: pmpSellLine,
          //   borderColor: 'rgba(255, 99, 132, 0.8)',
          //   borderWidth: 2,
          //   fill: false,
          //   pointRadius: 0,
          //   borderDash: [8, 4],
          //   type: 'line',
          //   yAxisID: 'y1'
          // },
          {
            label: 'PMP Units',
            data: pmpUnitLine,
            borderColor: 'rgba(54, 162, 235, 0.8)',
            borderWidth: 2,
            fill: false,
            pointRadius: 0,
            borderDash: [4, 4],
            type: 'line',
            yAxisID: 'y1'
          },
          {
            label: 'LLP',
            data: llpLine,
            borderColor: 'rgba(153, 102, 255, 0.8)',
            borderWidth: 2,
            fill: false,
            pointRadius: 0,
            borderDash: [2, 2],
            type: 'line',
            yAxisID: 'y2'
          }
        ]
      };
    },
    optionsSellRebuy() {
      const data = this.densidadeSellRb.datasets[0]?.data || [];
      const maxY = data.length ? Math.max(...data) : 1;
      const yMaxRespiro = maxY * 1.25;

      return {
        responsive: true,
        interaction: {
          intersect: false,
          mode: 'index',
        },
        elements: {
          point: { radius: 0 },
          line: { tension: 0.3, borderWidth: 2 }
        },
        plugins: {
          legend: { position: 'top' },
          tooltip: {
            backgroundColor: 'rgba(255, 236, 179, 0.8)',
            bodyColor: 'rgba(0, 0, 0, 0.6)',
            titleColor: 'rgba(0, 0, 0, 0.6)',
            callbacks: {
              label: function(context) {
                const value = context.parsed.y;
                const label = context.dataset.label;
                if (label === 'PMP Rebuys Sells') {
                  return `Quantidade: ${Number(value).toFixed(5)}`;
                } else if (label === 'PMP Sells') {
                  return `PMP Sells: ${Number(value).toFixed(2)}`;
                } else if (label === 'PMP Units') {
                  return `PMP Units: ${Number(value).toFixed(2)}`;
                } else if (label === 'LLP') {
                  return `LLP: ${Number(value).toFixed(4)}`;
                } else {
                  return `${label}: ${Number(value).toFixed(6)}`;
                }
              }
            }
          }
        },
        scales: {
          x: { title: { display: false, text: 'Preço (faixa)' } },
          y: {
            title: { display: false, text: 'Quantidade' },
            min: 0,
            max: yMaxRespiro,
            position: 'left',
          },
          y1: {
            title: { display: false, text: 'Preço Médio' },
            position: 'right',
            grid: { drawOnChartArea: false },
            min: 0,
          },
          y2: {
            title: { display: false, text: 'LLP' },
            position: 'right',
            grid: { drawOnChartArea: false },
            offset: true
          }
        }
      };
    }
  },
}
</script>
